import {
  Alert,
  Anchor,
  Badge,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useOrgScopedQuery } from "../../query/scope";
import {
  probeRepoHostToken,
  type CapabilityState,
  type ProbedCapabilitiesResponse,
} from "./tokensApi";

export interface ProbeModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly role: string;
}

// Цвет Badge по тристейту capability.
function capabilityColor(state: CapabilityState): string {
  if (state === "supported") return "green";
  if (state === "unsupported") return "red";
  return "gray";
}

// Текст Badge по тристейту capability.
function CapabilityBadge(props: {
  readonly state: CapabilityState;
}): ReactElement {
  const { t } = useTranslation();
  const { state } = props;
  const label =
    state === "supported"
      ? t("connections.tokens.probe.stateSupported")
      : state === "unsupported"
        ? t("connections.tokens.probe.stateUnsupported")
        : t("connections.tokens.probe.stateUnknown");
  return <Badge color={capabilityColor(state)}>{label}</Badge>;
}

// Тело модалки: запускает probe и показывает результат.
function ProbeModalBody(props: { readonly role: string }): ReactElement {
  const { t } = useTranslation();
  const { role } = props;

  const probeQuery = useOrgScopedQuery<ProbedCapabilitiesResponse>({
    key: ["connections", "forgejo", "probe", role],
    queryFn: () => probeRepoHostToken(role),
    retry: false,
  });

  if (probeQuery.isPending) {
    return <Loader />;
  }

  if (probeQuery.isError) {
    return <Alert color="red">{t("connections.loadingError")}</Alert>;
  }

  const data = probeQuery.data;
  const showAdminWarning = data.is_admin === "supported";
  const showWriteHint = data.can_write_repository === "unknown";

  return (
    <Stack gap="sm">
      <Text size="sm">
        {t("connections.tokens.probe.owner")}:{" "}
        <strong>{data.host_username}</strong>
      </Text>

      {showAdminWarning ? (
        <Alert color="yellow">
          {t("connections.tokens.probe.warning")}{" "}
          <Anchor component={Link} to="/settings/agent-roles" size="sm">
            {t("connections.tokens.probe.warningLink")}
          </Anchor>
        </Alert>
      ) : null}

      {showWriteHint ? (
        <Alert color="blue">{t("connections.tokens.probe.writeHint")}</Alert>
      ) : null}

      <Table withTableBorder withColumnBorders>
        <Table.Tbody>
          <Table.Tr>
            <Table.Td>{t("connections.tokens.probe.readRepo")}</Table.Td>
            <Table.Td>
              <CapabilityBadge state={data.can_read_repository} />
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td>{t("connections.tokens.probe.writeRepo")}</Table.Td>
            <Table.Td>
              <CapabilityBadge state={data.can_write_repository} />
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td>{t("connections.tokens.probe.readIssue")}</Table.Td>
            <Table.Td>
              <CapabilityBadge state={data.can_read_issue} />
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td>{t("connections.tokens.probe.writeIssue")}</Table.Td>
            <Table.Td>
              <CapabilityBadge state={data.can_write_issue} />
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td>{t("connections.tokens.probe.deleteRepo")}</Table.Td>
            <Table.Td>
              <CapabilityBadge state={data.can_delete_repository} />
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td>
              {t("connections.tokens.probe.deleteProtectedBranch")}
            </Table.Td>
            <Table.Td>
              <CapabilityBadge state={data.can_delete_protected_branch} />
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td>{t("connections.tokens.probe.isAdmin")}</Table.Td>
            <Table.Td>
              <CapabilityBadge state={data.is_admin} />
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

// Модалка результата POST /probe токена роли.
export function ProbeModal(props: ProbeModalProps): ReactElement {
  const { t } = useTranslation();
  const { opened, onClose, role } = props;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("connections.tokens.probe.title", { role })}
      size="md"
    >
      {opened ? <ProbeModalBody role={role} /> : null}
    </Modal>
  );
}
